/**
 * MAHI — "Sign in with Google", via Google Identity Services directly.
 *
 * Renders Google's own button (through `google.accounts.id.renderButton`)
 * rather than a custom-styled one: GIS only reliably opens its consent
 * popup from a click on the real button it controls, and rendering the
 * genuine button is also what Google's branding terms require. `onCredential`
 * is handed the signed ID token straight from Google's callback — nothing
 * here decodes or trusts it, the backend verifies it against Google's own
 * public keys.
 */
import React, { useEffect, useRef } from 'react';

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function loadGsiScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!window.__mahiGsiLoading) {
    window.__mahiGsiLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }
  return window.__mahiGsiLoading;
}

export default function GoogleSignInButton({ onCredential, text = 'continue_with' }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !containerRef.current) return;
    let cancelled = false;

    loadGsiScript().then(() => {
      if (cancelled || !containerRef.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => onCredential(response.credential),
        ux_mode: 'popup',
      });
      window.google.accounts.id.renderButton(containerRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text,
        logo_alignment: 'left',
        width: Math.min(400, Math.round(containerRef.current.getBoundingClientRect().width) || 320),
      });
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [onCredential, text]);

  return <div ref={containerRef} className="w-full flex justify-center" data-testid="mahi-google-signin-btn" />;
}
